import { useCallback, useMemo, useState } from 'react';
import {
  getAncestorPaths,
  NestedOptions,
  searchNestedOptionsTree,
} from '@/storybook/components/FormCollection/TreeSelect/TreeSelect.helpers';

type UseTreeSelectHandlersParams<T extends string> = {
  value: T[];
  onChange: (value: T[]) => void;
  nestedOptions: NestedOptions;
  descendantMap: Map<string, string[]>;
  selectionMode: 'standard' | 'indication';
  hideCheckboxes?: boolean;
  optionLabels?: ReadonlyMap<string, string>;
};

export function useTreeSelectHandlers<T extends string>({
  value,
  onChange,
  nestedOptions,
  descendantMap,
  selectionMode,
  hideCheckboxes,
  optionLabels,
}: UseTreeSelectHandlersParams<T>) {
  const [searchInput, setSearchInput] = useState('');
  // Stores the user's explicit expand/collapse intent per branch path.
  // true = user opened, false = user closed. Absent = no override (use auto-open).
  const [branchOverrides, setBranchOverrides] = useState<Map<string, boolean>>(new Map());

  const searchResults = useMemo(
    () => searchNestedOptionsTree(nestedOptions, searchInput, optionLabels),
    [searchInput, nestedOptions, optionLabels],
  );

  // Clear any user-closed overrides on ancestor branches so newly
  // selected descendants auto-expand their parent chain.
  const clearAncestorOverrides = useCallback((optionPath: string) => {
    const ancestors = getAncestorPaths(optionPath);
    if (ancestors.length === 0) return;
    setBranchOverrides(prev => {
      const hasClosedAncestor = ancestors.some(ancestorPath => prev.get(ancestorPath) === false);
      if (!hasClosedAncestor) return prev;
      const next = new Map(prev);
      ancestors.forEach(ancestorPath => {
        if (next.get(ancestorPath) === false) next.delete(ancestorPath);
      });
      return next;
    });
  }, []);

  const handleClickIndicationMode = useCallback(
    (optionPath: T) => {
      const newSelected = new Set(value);
      const selecting = !newSelected.has(optionPath);
      const itemsToToggle = [optionPath, ...(descendantMap.get(optionPath) || [])] as T[];

      for (const item of itemsToToggle) {
        if (selecting) newSelected.add(item);
        else newSelected.delete(item);
      }

      if (selecting) clearAncestorOverrides(optionPath);
      onChange(Array.from(newSelected));
    },
    [value, descendantMap, onChange, clearAncestorOverrides],
  );

  const handleClickOption = useCallback(
    (optionPath: string) => {
      if (selectionMode === 'indication') {
        return handleClickIndicationMode(optionPath as T);
      }

      if (hideCheckboxes) {
        const isAlreadySelected = value.includes(optionPath as T);
        onChange(isAlreadySelected ? [] : [optionPath as T]);
        return;
      }

      const newSelected = new Set(value);
      const selecting = !newSelected.has(optionPath as T);

      if (selecting) {
        newSelected.add(optionPath as T);
        clearAncestorOverrides(optionPath);
      } else {
        newSelected.delete(optionPath as T);
      }

      onChange(Array.from(newSelected));
    },
    [selectionMode, handleClickIndicationMode, hideCheckboxes, value, onChange, clearAncestorOverrides],
  );

  const handleToggleBranch = useCallback((optionPath: string, currentState: boolean) => {
    const newState = !currentState;
    setBranchOverrides(prev => {
      if (prev.get(optionPath) === newState) return prev;
      return new Map(prev).set(optionPath, newState);
    });
  }, []);

  return {
    searchInput,
    setSearchInput,
    searchResults,
    branchOverrides,
    setBranchOverrides,
    handleClickOption,
    handleToggleBranch,
  };
}
